import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssistanceFieldContext } from '../../document/pages/Assistance/AssistanceFieldContext.tsx';
import useForceSelectionLookupProps, {
    useForceSelectionReselectEventListener,
} from '../../document/components/useForceSelectionLookupProps';
import AutoCompleteField from '../../core/components/Fields/AutoCompleteField';
import { FieldProps, useFieldProps } from '../../document/pages/Assistance/customizable/Field';
import { renderArticleIdOption } from '../../document/components/useLookup';
import { useDefaultComponents } from './useDefaultComponents.tsx';

/**
 * Wrapper for the article_partition_specific_number (Component) field in Siemens List of Services.
 *
 * When the field is empty (no search term), the regular empty-search is suppressed and replaced
 * by an "Often used" section containing up to 5 pre-fetched default components for the current
 * rail system partition. When the user types, regular search results are shown instead.
 *
 * allowInvalidIds is set to true while showing defaults so that useForceSelection does not
 * validate (and clear) a selected default option against the empty lookup results.
 *
 * onValueChange is intentionally omitted from useForceSelectionLookupProps so that typing does
 * not propagate up through useControllableState and trigger re-validation loops. The search term
 * is managed internally by the hook.
 */
export const SiemensComponentFieldWrapper = React.memo(function SiemensComponentFieldWrapper(props: FieldProps) {
    const { t } = useTranslation('assistance');
    const assistanceFieldContext = useAssistanceFieldContext();
    const {
        lookupFieldName,
        lookupDefinitionId,
        partitionId: configPartitionId,
        allowInvalidIds: configAllowInvalidIds,
        resetInvalidOnBlur: configResetInvalidOnBlur,
    } = assistanceFieldContext.config;

    const { defaultOptions, isLoadingDefaults } = useDefaultComponents({
        lookupDefinitionId,
        lookupFieldName,
        partitionId: configPartitionId,
    });

    const fieldProps = useFieldProps(props);

    // Track the current input text to correctly compute allowInvalidIds before the hook is called.
    // props.value (committed value) cannot be used here: since onValueChange is omitted from the
    // hook, props.value stays at the last committed value during typing and would incorrectly keep
    // forceSelection=true while the field is visually empty, causing default selections to be
    // immediately invalidated by useForceSelection's options-change effect.
    const [inputValue, setInputValue] = useState(props.value || '');
    useEffect(() => {
        setInputValue(props.value || '');
    }, [props.value]);

    const forceSelectionLookupProps = useForceSelectionLookupProps({
        ...assistanceFieldContext.config,
        lookupType: assistanceFieldContext.config.lookupType,
        lookupDefinitionId,
        lookupFieldName,
        partitionId: configPartitionId,
        allowInvalidIds: configAllowInvalidIds || (!inputValue && defaultOptions.length > 0),
        resetInvalidOnBlur: configResetInvalidOnBlur,
        value: props.value,
        onValueChange: configAllowInvalidIds ? props.onValueChange : undefined,
        onUpdatePayloadChange: props.onUpdatePayloadChange,
        onFocus: fieldProps.onFocus,
        onBlur: fieldProps.onBlur,
        onCommit: fieldProps.onCommit,
        lookupMatchingProps: {
            ...assistanceFieldContext.config,
            searchLookupOnEmptyValue: !isLoadingDefaults && defaultOptions.length === 0,
            // Only force the browser button while defaults are shown (empty input). Once the user
            // types, fall back to the default behavior so a "no results" search doesn't render it.
            alwaysShowBrowser: !inputValue,
        },
    });

    const handleValueChange = (value: string) => {
        setInputValue(value || '');
        forceSelectionLookupProps.onValueChange(value);
    };

    // Use the hook's live input value for display decisions — it updates immediately on selection
    // (via handleSelectOption → updateInputValue), whereas local inputValue only updates via typing.
    const isShowingDefaults = !forceSelectionLookupProps.value && defaultOptions.length > 0;

    const displayOptions = useMemo(() => {
        if (!isShowingDefaults) {
            return forceSelectionLookupProps.options || [];
        }

        return [
            {
                role: 'message',
                label: t('listOfServices.articleSetCard.oftenUsed'),
                value: '_often_used_header',
            },
            ...defaultOptions,
            // Append the "See more results" button supplied by the lookup hook (alwaysShowBrowser).
            // When defaults are shown, searchLookupOnEmptyValue is false so the lookup options array
            // contains only the browser button; spreading it preserves the existing click wiring.
            ...(forceSelectionLookupProps.options || []),
        ];
    }, [isShowingDefaults, forceSelectionLookupProps.options, defaultOptions, t]);

    useForceSelectionReselectEventListener(props.inputRef, handleValueChange);

    return (
        <AutoCompleteField
            {...fieldProps}
            {...forceSelectionLookupProps}
            onValueChange={handleValueChange}
            options={displayOptions}
            isLoading={isShowingDefaults ? isLoadingDefaults : forceSelectionLookupProps.isLoading}
            renderOption={renderArticleIdOption(lookupFieldName)}
        />
    );
});
